import { css, cx } from "@emotion/css";
import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { SimpleDataTable, ActionButtonType, Pagination, Button, Tooltip, Badge, TagGroup } from "@octopusdeploy/design-system-components";
import { ExperimentIcon } from "@octopusdeploy/design-system-icons";
import { Avatar, FilterSearchBox } from "@octopusdeploy/design-system-octopus-components";
import { borderRadius, fontSize, fontWeight, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import type { Repository, PlatformHubVersionControlSettingsResource, ValidateGitRefV2Response, GitRef, GetProjectTemplatesBffResponse, ProjectTemplateSummary, ProjectTemplateUsage, IconResource } from "@octopusdeploy/octopus-server-client";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import type { ReactElement } from "react";
import { lastAccessedPlatformHubGitRef } from "~/areas/platformHub/LastAccessedPlatformHubGitRef";
import { PlatformHubPageHeaderBranchSelector } from "~/areas/platformHub/PlatformHubPageHeaderBranchSelector";
import { TemplateNameCell } from "~/areas/platformHub/TemplateNameCell";
import { TemplateOverflowMenu } from "~/areas/platformHub/TemplateOverflowMenu";
import { NewProjectTemplateWizard } from "~/areas/platformHub/projectTemplates/NewProjectTemplateWizard";
import { usePlatformHubGitRef } from "~/areas/platformHub/usePlatformHubGitRef";
import { repository } from "~/clientInstance";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import OnboardingPage from "~/components/GettingStarted/OnboardingPage";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useOctopusTheme } from "~/components/Theme";
import { getValueInStorage } from "~/hooks/localStorageHelpers";
import useLocalStorage from "~/hooks/useLocalStorage";
import DateFormatter from "~/utils/DateFormatter/index";
import type { ViewProjectTemplateUsagesWorkflowInput } from "~/workflows/ViewProjectTemplateUsagesWorkflowInput";
import { WorkflowContent } from "~/workflows/WorkflowContent";
import { useWorkflow } from "~/workflows/useWorkflow";
import { workflows } from "~/workflows/workflows";
interface LoaderData {
    projectTemplatesResponse: GetProjectTemplatesBffResponse;
    persistenceSettings: PlatformHubVersionControlSettingsResource;
    initialValidationResult: ValidateGitRefV2Response | undefined;
}
interface ProjectTemplatesPageProps {
    loaderData: LoaderData;
    queryParams: ProjectTemplatesPageQueryParams;
    setQueryParams: QueryParamValuesSetter<ProjectTemplatesPageQueryParams>;
}
export interface ProjectTemplatesPageQueryParams {
    searchValue: string;
    page: number | undefined;
    pageSize: number | undefined;
    gitRef: string;
}
const defaultPageSize = 30;
const emptyProjectTemplatesResponse: GetProjectTemplatesBffResponse = {
    ProjectTemplates: [],
    TotalResults: 0,
    ItemsPerPage: 0,
    TotalNoOfProjectTemplates: 0,
};
export async function projectTemplatesPageLoader(repository: Repository, queryParams: ProjectTemplatesPageQueryParams): Promise<LoaderData> {
    const persistenceSettings = await repository.PlatformHub.getVersionControlSettings();
    if (!persistenceSettings.Url) {
        return {
            projectTemplatesResponse: emptyProjectTemplatesResponse,
            persistenceSettings,
            initialValidationResult: undefined,
        };
    }
    let gitRef = queryParams.gitRef;
    if (!gitRef) {
        gitRef = lastAccessedPlatformHubGitRef.get(persistenceSettings);
    }
    const initialValidationResult = await repository.PlatformHub.validateGitRef(gitRef);
    const userPageSize = getValueInStorage(`Octopus.ProjectTemplatesList.PageSize`);
    const pageSize = userPageSize ? parseInt(userPageSize) : defaultPageSize;
    const projectTemplatesResponse = await repository.ProjectTemplates.getPaginatedTemplates({
        skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? pageSize),
        take: queryParams.pageSize ?? pageSize,
        partialName: queryParams.searchValue,
        gitRef: initialValidationResult.GitRef?.CanonicalName ?? gitRef,
    });
    return {
        projectTemplatesResponse,
        persistenceSettings,
        initialValidationResult,
    };
}
export function ProjectTemplatesPage({ loaderData, queryParams, setQueryParams }: ProjectTemplatesPageProps) {
    const [gitRef, setGitRef] = usePlatformHubGitRef(loaderData.persistenceSettings, queryParams, setQueryParams, loaderData.initialValidationResult);
    const [userDefaultPageSize, setUserDefaultPageSize] = useLocalStorage(`Octopus.ProjectTemplatesList.PageSize`, defaultPageSize);
    const { result: pagingData, refetch: reload } = useQuery((repository) => {
        if (!gitRef) {
            return Promise.resolve(emptyProjectTemplatesResponse);
        }
        return repository.ProjectTemplates.getPaginatedTemplates({
            skip: ((queryParams.page ?? 1) - 1) * (queryParams.pageSize ?? userDefaultPageSize),
            take: queryParams.pageSize ?? userDefaultPageSize,
            partialName: queryParams.searchValue,
            gitRef,
        });
    }, [gitRef, queryParams.page, queryParams.pageSize, queryParams.searchValue, userDefaultPageSize], "Get paginated project templates", {
        initialResult: loaderData.projectTemplatesResponse,
    });
    const onPageChange = (page: number) => {
        setQueryParams({ ...queryParams, page });
    };
    const onPageSizeChange = (pageSize: number) => {
        setQueryParams({ ...queryParams, pageSize: pageSize, page: 1 });
        setUserDefaultPageSize(pageSize);
    };
    const onSearch = (value: string) => {
        setQueryParams({ ...queryParams, searchValue: value, page: 1 });
    };
    const { execute: deleteProjectTemplate } = useMutation({
        name: "Delete Project Template",
        action: (repository: Repository, projectTemplate: ProjectTemplateSummary) => repository.ProjectTemplates.del({ Slug: projectTemplate.Slug, GitRef: projectTemplate.GitRef, ChangeDescription: `Delete Project Template '${projectTemplate.Name}'` }),
        afterComplete: async () => reload(),
    });
    if (!gitRef || !loaderData.persistenceSettings.Url) {
        return (<PageContent header={getProjectTemplatesHeader()}>
                <ConfigureVersionControlOnboarding />
            </PageContent>);
    }
    const addProjectTemplateAction: PrimaryPageAction = {
        type: "custom",
        key: "Create Project Template",
        hasPermissions: isAllowed({ permission: Permission.PlatformHubEdit }),
        content: <AddProjectTemplateButton gitRef={gitRef}/>,
    };
    const showOnboarding = pagingData.TotalNoOfProjectTemplates === 0;
    const titleAccessory = <PlatformHubPageHeaderBranchSelector setGitRef={setGitRef} gitRef={gitRef} persistenceSettings={loaderData.persistenceSettings}/>;
    if (showOnboarding) {
        return (<PageContent header={getProjectTemplatesHeader(titleAccessory)}>
                <AddProjectTemplatesOnboarding gitRef={gitRef}/>
            </PageContent>);
    }
    return (<PageContent header={getProjectTemplatesHeader(titleAccessory, [addProjectTemplateAction])} filters={{
            inputs: [<FilterSearchBox value={queryParams.searchValue} onChange={onSearch} placeholder={"Filter by name..."} autoFocus={false} debounceDelay={500}/>],
        }} pagination={{
            ui: (<Pagination label="Project Templates" totalResults={pagingData.TotalResults} itemsPerPageOptions={[30, 50, 100]} selectedItemsPerPage={pagingData.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: "bottom",
        }}>
            <ProjectTemplatesTable projectTemplates={pagingData.ProjectTemplates} deleteProjectTemplate={deleteProjectTemplate}/>
        </PageContent>);
}
interface ProjectTemplatesTableProps {
    projectTemplates: ProjectTemplateSummary[];
    deleteProjectTemplate: (projectTemplate: ProjectTemplateSummary) => Promise<void>;
}
export function ProjectTemplatesTable({ projectTemplates, deleteProjectTemplate }: ProjectTemplatesTableProps) {
    return (<SimpleDataTable data={projectTemplates} columns={[
            {
                title: "Name",
                render: (projectTemplate) => (<TemplateNameCell name={projectTemplate.Name} icon={projectTemplate.Icon} linkTarget={links.projectTemplateDeploymentProcessPage.generateUrl({ slug: projectTemplate.Slug, gitRef: projectTemplate.GitRef })}/>),
                columnSize: "medium",
            },
            {
                title: "Version",
                render: (projectTemplate) => (projectTemplate.Version ? <div className={styles.rowCellPadding}>{projectTemplate.Version}</div> : null),
                columnSize: "x-small",
            },
            {
                title: "Last Published",
                render: (projectTemplate) => (projectTemplate.PublishedDate ? <div className={styles.rowCellPadding}>{DateFormatter.dateToLongFormat(projectTemplate.PublishedDate)}</div> : null),
                columnSize: "medium",
            },
            {
                title: "Usage",
                render: (projectTemplate) => (<div className={cx(styles.rowCellPadding, styles.usageContainer)} style={projectTemplate.Usages.length > 0 ? { justifyContent: "space-between" } : { justifyContent: "end" }}>
                            <ViewProjectTemplateUsagesTrigger projectTemplate={projectTemplate} usages={projectTemplate.Usages}/>
                            <TemplateOverflowMenu templateName={projectTemplate.Name} templateTypeLabel="project template" onDelete={() => deleteProjectTemplate(projectTemplate)}/>
                        </div>),
                columnSize: "medium",
            },
        ]} getRowKey={(projectTemplate) => projectTemplate.Slug}/>);
}
const getProjectTemplatesHeader = (contextSelector?: ReactElement, pageActions?: PageAction[]) => {
    const alphaBadge = (<div className={styles.badgeContainer}>
            <Tooltip content={"This feature is not finished or fully tested."}>
                <Badge variant={"feature"} size={"medium"} label={"Alpha"} icon={<ExperimentIcon size={20} color={themeTokens.color.badge.icon.feature}/>}/>
            </Tooltip>
        </div>);
    return { title: "Project Templates", contextSelector, chip: alphaBadge, pageActions };
};
//TODO put correct link to docs in callout
const AddProjectTemplatesOnboarding = ({ gitRef }: {
    gitRef: GitRef;
}) => (<OnboardingPage title="Create your first project template" intro="Project templates are a group of steps that are reusable across projects." learnMore={null} actionButtons={<AddProjectTemplateButton gitRef={gitRef}/>}/>);
const ConfigureVersionControlOnboarding = () => (<OnboardingPage title="Create your first project template" intro="Project templates are a group of steps that are reusable across projects. Project templates are stored in a Git repository. To add a project template, you'll first need to set up a Git connection in the Platform Hub." learnMore={null} actionButtons={<Button label={"Configure Version Control"} href={links.platformHubVersionControlSettingsPage.generateUrl()} importance="primary"/>}/>);
function AddProjectTemplateButton({ gitRef }: {
    gitRef: GitRef;
}) {
    return (<OpenDialogButton disabled={false} label={"Create Project Template"} title={"Create new Project Template"} type={ActionButtonType.Primary} renderDialog={({ open, closeDialog }) => open && <NewProjectTemplateWizard gitRef={gitRef} closeDialog={closeDialog}/>}/>);
}
interface ViewProjectTemplateUsagesTriggerProps {
    projectTemplate: ProjectTemplateSummary;
    usages: ProjectTemplateUsage[];
}
const ViewProjectTemplateUsagesTrigger = ({ projectTemplate, usages }: ViewProjectTemplateUsagesTriggerProps) => {
    const startViewProjectTemplateUsagesWorkflow = useWorkflow(workflows.viewProjectTemplateUsagesWorkflow);
    const spaceNames = [...new Set(usages?.map((u) => u.Space.Name))];
    const spaceNameToIconMap = usages.reduce((acc, usage) => {
        if (usage.Space.Icon) {
            acc[usage.Space.Name] = usage.Space.Icon;
        }
        return acc;
    }, 
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    {} as Record<string, IconResource | undefined>);
    const triggerStyles = {
        button: css({
            background: "none",
            border: "none",
            padding: 0,
            margin: 0,
            cursor: "pointer",
            font: "inherit",
            color: "inherit",
            outline: "inherit",
        }),
    };
    const onClick = () => startViewProjectTemplateUsagesWorkflow({ projectTemplate, usages });
    if (spaceNames.length === 0) {
        return null;
    }
    return (<button className={cx(triggerStyles.button, styles.rowCellPadding)} onClick={onClick}>
            <TagGroup tags={spaceNames.map((spaceName) => ({
            label: spaceName,
            prefix: <Avatar type="space" size="small" src={spaceNameToIconMap[spaceName] ? repository.Icons.getIconUrl(spaceNameToIconMap[spaceName]) : undefined} alt={`Icon of the space ${spaceName} using the project template`}/>,
        }))} limit={{ count: 2, link: "#" }}/>
        </button>);
};
interface ViewProjectTemplateUsagesWorkflowProps {
    input: ViewProjectTemplateUsagesWorkflowInput;
    completeWorkflow: () => void;
}
export const ViewProjectTemplateUsagesWorkflow = ({ input, completeWorkflow }: ViewProjectTemplateUsagesWorkflowProps) => {
    const { projectTemplate, usages } = input;
    const spaceUsageCount = new Set(usages?.map((u) => u.Space.Id)).size;
    const projectUsageCount = usages.length;
    const workflowStyles = {
        tableContainer: css({
            border: `1px solid ${useOctopusTheme().dividerLight}`,
            borderRadius: borderRadius.small,
            marginTop: space[16],
            marginBottom: space[16],
            maxHeight: `415px`,
            overflowY: `auto`,
        }),
        spaceOrProjectTableCellContainer: css({
            display: "flex",
            alignItems: "center",
            gap: space[12],
        }),
        spaceOrProjectTableCellText: css({
            fontSize: fontSize.medium,
            fontWeight: fontWeight[400],
        }),
    };
    return (<WorkflowContent.Dialog workflow={workflows.viewProjectTemplateUsagesWorkflow} title={`${projectUsageCount} ${projectUsageCount === 1 ? "project" : "projects"} across ${spaceUsageCount} ${spaceUsageCount === 1 ? "space" : "spaces"} ${projectUsageCount === 1 ? "is" : "are"} using "${projectTemplate.Name}"`} primaryAction={{ label: "Close", accessibleName: "Close project template usages dialog", onClick: completeWorkflow }}>
            <div>
                {usages.length > 0 && (<div className={workflowStyles.tableContainer}>
                        <SimpleDataTable data={usages} columns={[
                {
                    title: "Space",
                    render: (usage) => (<div className={workflowStyles.spaceOrProjectTableCellContainer}>
                                            <Avatar type="space" size="medium" src={repository.Icons.getIconUrl(usage.Space.Icon)} alt="Icon of a space using the project template"/>
                                            <div className={workflowStyles.spaceOrProjectTableCellText}>{usage.Space.Name}</div>
                                        </div>),
                },
                {
                    title: "Project",
                    render: (usage) => (<div className={workflowStyles.spaceOrProjectTableCellContainer}>
                                            <Avatar type="project" size="medium" src={repository.Icons.getIconUrl(usage.Project.Icon)} alt="Icon of a project using the project template"/>
                                            <div className={workflowStyles.spaceOrProjectTableCellText}>{usage.Project.Name}</div>
                                        </div>),
                },
                {
                    title: "Current version",
                    render: (usage) => usage.Project.ProjectTemplateDetails && usage.Project.ProjectTemplateDetails.Version,
                },
            ]} getRowKey={(usage) => usage.Project.Id}/>
                    </div>)}
            </div>
        </WorkflowContent.Dialog>);
};
const styles = {
    rowCellPadding: css({ paddingLeft: space[1] }),
    badgeContainer: css({
        lineHeight: "0",
    }),
    usageContainer: css({
        display: "flex",
        alignItems: "center",
    }),
};
